import { debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type React from 'react';

/**
 * Drag-and-seek state machine for scrubber rails — transport bars, safe-zone
 * overlays, or any surface that needs click-and-drag seeking against a
 * `PlaybackBridge`.
 *
 * Why a hook instead of inlining:
 * - Multiple overlays + the clip preview transport bar share the same drag
 *   pattern. Diverging implementations risk one path getting a fix the other
 *   misses.
 * - The drag dance is finicky enough (seek-time precedence, window-level
 *   listener wiring, ready-state gating, unmount cleanup) that one
 *   well-tested copy is safer than two near-copies.
 *
 * Behavior:
 * - `displayTime` follows the cursor in real time during a drag (the
 *   renderer's `onTimeChange` echo can be delayed by the iframe round-trip,
 *   making the thumb appear to snap on release without this).
 * - `mousemove` / `mouseup` listeners are attached to **window** rather than
 *   the rail so the drag continues even if the cursor leaves the rail mid-
 *   gesture (standard slider behavior).
 * - The drag's `mouseup` clears the listeners. If the component unmounts
 *   mid-drag, the cleanup `useEffect` strips any active listeners so they
 *   don't leak.
 *
 * @param playback The optional playback bridge. When undefined / not ready,
 * `onMouseDown` becomes a no-op and `scrubberInteractive` is `false` so the
 * rail can disable its hit area visually.
 * @param options Optional configuration (e.g. debounce interval for setTime).
 */
export interface UseScrubberDragOptions {
  /**
   * Debounce interval (ms) for `setTime` calls during a drag. When > 0,
   * `setSeekTime` still fires immediately on every move (keeping the visual
   * fill smooth) but the expensive renderer seek is throttled. On mouseup the
   * debounce is cancelled and a final `setTime` fires immediately.
   *
   * Default: `0` (immediate, no debounce).
   */
  debounceMs?: number;
}

export interface UseScrubberDrag {
  /** Bind to the scrubber rail's DOM element to make rect math work. */
  scrubberRef: React.RefObject<HTMLDivElement>;
  /** Time to render — drag-aware (`seekTime` while dragging, otherwise live). */
  displayTime: number;
  /** Progress percentage (`0`–`100`) for the fill width. */
  progressPct: number;
  /** Whether the rail should accept clicks / drags. */
  scrubberInteractive: boolean;
  /** Mousedown handler to wire on the rail element. */
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * The time under a given viewport x, clamped to `[0, duration]`, or `null`
   * when the rail isn't measurable yet. Exposed so a consumer can answer
   * "what's under the cursor?" for hover affordances without duplicating the
   * rail's rect math — the drag itself still owns all of the state.
   */
  computeTimeFromClientX: (clientX: number) => number | null;
}

export function useScrubberDrag(
  playback: PlaybackBridge | undefined,
  options?: UseScrubberDragOptions,
): UseScrubberDrag {
  const { debounceMs = 0 } = options ?? {};
  const currentTime = playback?.currentTime ?? 0;
  const duration = playback?.duration ?? 0;
  const isReady = playback?.isReady ?? false;
  const seekTime = playback?.seekTime ?? null;
  const isSeeking = playback?.isSeeking ?? false;
  const scrubberRef = useRef<HTMLDivElement | null>(null);
  // Holds the current drag's window listeners so a mid-drag unmount can
  // detach them. `null` between drags.
  const dragListenersRef = useRef<{
    onMove: (ev: MouseEvent) => void;
    onUp: (ev: MouseEvent) => void;
  } | null>(null);

  const displayTime = seekTime != null ? seekTime : currentTime;
  const progressPct =
    duration > 0 ? Math.min(100, (displayTime / duration) * 100) : 0;

  // Strip any active listeners on unmount so a drag in progress doesn't
  // leave a window-level mousemove handler attached to a destroyed
  // component.
  useEffect(() => {
    return () => {
      const active = dragListenersRef.current;
      if (!active) return;
      window.removeEventListener('mousemove', active.onMove);
      window.removeEventListener('mouseup', active.onUp);
      dragListenersRef.current = null;
    };
  }, []);

  // Debounced setTime — when debounceMs > 0, setSeekTime fires immediately
  // (visual) while setTime (renderer seek) is throttled. The identity is
  // stable across renders for a given debounceMs.
  const debouncedSetTime = useMemo(
    () =>
      debounceMs > 0
        ? debounce((time: number) => playback?.setTime(time), debounceMs)
        : null,
    [debounceMs],
  );

  // Cancel any pending debounced call on unmount.
  useEffect(() => () => debouncedSetTime?.cancel(), [debouncedSetTime]);

  const seekImmediate = (time: number) => {
    if (!playback) return;
    if (debouncedSetTime) {
      debouncedSetTime(time);
    } else {
      playback.setTime(time);
    }
  };

  const computeTimeFromClientX = (clientX: number): number | null => {
    const el = scrubberRef.current;
    if (!el || duration <= 0) return null;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return null;
    const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return pct * duration;
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!playback || !isReady || duration <= 0) return;
    e.preventDefault();
    const initial = computeTimeFromClientX(e.clientX);
    if (initial == null) return;

    // Capture and pause so `currentTime` stops advancing during the drag.
    // Without this, the underlying renderer keeps playing and on mouseup the
    // thumb snaps to wherever playback has reached, oscillating off the
    // user's drop target. Resume on mouseup only if we were playing.
    const wasPlaying = playback.isPlaying;
    playback.setIsSeeking(true);
    playback.setSeekTime(initial);
    seekImmediate(initial);
    if (wasPlaying) playback.pause();

    const onMove = (ev: MouseEvent) => {
      if (!isSeeking) return;
      const next = computeTimeFromClientX(ev.clientX);
      if (next == null) return;
      playback.setSeekTime(next);
      seekImmediate(next);
    };
    const onUp = (ev: MouseEvent) => {
      debouncedSetTime?.cancel();
      playback.setIsSeeking(false);
      const final = computeTimeFromClientX(ev.clientX);
      if (final != null) playback.setTime(final);
      if (wasPlaying) playback.play();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragListenersRef.current = null;
    };
    dragListenersRef.current = { onMove, onUp };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const scrubberInteractive = !!playback && isReady && duration > 0;

  return {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown,
    computeTimeFromClientX,
  };
}
